import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Form, Button, Col, Tooltip, Overlay } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { NEW_API_URL } from "../../constants/Globals";
import { isSubscriptionFeatureLocked } from "../../services/NetworkSvc";
import SvgElement from "./debatePreviewV2/SvgElement";
import { isUserHasValidRoleForGroup } from "../../services/GroupSvc";
import { showSubscriptionAlertPopup } from "../../services/SubscriptionAlertPopupSvc";
import {
  fetchAiRequestLimitDetail,
  fetchAiResponseLimitDetail,
  changeSubscriptionPlan,
  buySubscriptionPlan,
  manageSubscriptionPlan,
} from "../../services/SubscriptionSvc";
import SubscriptionModal from "../../components/SubscriptionModal";
import { handleSuccess } from "../../services/notifications";
import ArchiveOrganization from "./ArchiveOrganization";

const GroupOrganizationSettings = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const groupId = searchParams.get("groupId");
  const paymentResult = searchParams.get("payment");
  const [showTooltip, setShowTooltip] = useState(false);
  const iconRef = React.useRef(null);

  const [subscriptionModalVisible, setSubscriptionModalVisible] =
    useState(false);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [organizationName, setOrganizationName] = useState("");
  const [subscriptionTypeId, setSubscriptionTypeId] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [renewalDate, setRenewalDate] = useState(null);
  const [networksCountLimit, setNetworksCountLimit] = useState("");
  const [durationResponses, setDurationResponses] = useState("");
  const [durationAiRequests, setDurationAiRequests] = useState("");
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(false);
  const [isSendTagAndAudienceSegmToGAM, setIsSendTagAndAudienceSegmToGAM] = useState(false);
  const [hideSidedBranding, setHideSidedBranding] = useState(false);
  const [isAiResponseTagging, setIsAiResponseTagging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [isFeatureLockHideSidedBranding, setIsFeatureLockHideSidedBranding] =
    useState(false);
  const [aiReqData, setAiReqData] = useState(null);
  const [aiRespData, setAiRespData] = useState(null);

  const [isLoggedInUserAdmin, setIsLoggedInUserAdmin] = useState(false);
  const [isLoggedInUserSuperAdmin, setIsLoggedInUserSuperAdmin] =
    useState(false);
  const [subscriptionStartDate, setSubscriptionStartDate] = useState("");
  const [subscriptionEndDate, setSubscriptionEndDate] = useState("");
  const [subscriptionEndDateWithTime, setSubscriptionEndDateWithTime] =
    useState("");
  const [subscriptionMetaDetails, setSubscriptionMetaDetails] = useState(null);

  const fetchSubscriptionTypes = async () => {
    try {
      const url = `${NEW_API_URL}/master/subscription-types`;
      const res = await axios.get(url);
      if (res.data.status && res.data.data) {
        const subTypes = res.data.data;
        const subTypeMap = {};
        subTypes.forEach((type) => {
          subTypeMap[type.id] = type.name;
        });
        setSubscriptionTypes(subTypeMap);
      } else {
        alert(
          "There is a problem in fetching subscription types. Please try again!"
        );
      }
    } catch (err) {
      alert(
        "There is a problem in fetching subscription types. Please try again!!!!!"
      );
      console.error(err);
    }
  };

  const handleSubscriptionAcessPopup = async (showPopup) => {
    if (showPopup) {
      showSubscriptionAlertPopup({
        title: "Upgrade for Access",
        messageStr:
          "Your subscription plan does not include this feature. Please upgrade your subscription plan or contact support@sided.co if you feel this is an error.",
      });
    }
  };

  const fetchOrganizationSettings = async (groupId) => {
    setLoading(true);
    try {
      const url = `${NEW_API_URL}/master/group-settings/group/${groupId}?includeRelations=true`;
      const res = await axios.get(url);
      if (res.data.status && res.data.data) {
        const groupSettingDetail = res.data.data;
        const subscriptionPlanDuration =
          groupSettingDetail.subscription?.metadata?.selectedBillingCycle ??
          null;
        if (
          subscriptionPlanDuration &&
          subscriptionPlanDuration.toLowerCase() === "yearly"
        ) {
          setDurationResponses(groupSettingDetail.annualResponses || 0);
          setDurationAiRequests(groupSettingDetail.annualAiReports || 0);
        } else {
          setDurationResponses(groupSettingDetail.monthlyResponses || 0);
          setDurationAiRequests(groupSettingDetail.monthlyAiReports || 0);
        }
        setSubscriptionMetaDetails(groupSettingDetail.subscription.metadata);
        setOrganizationName(groupSettingDetail.group.name || "");
        setSubscriptionTypeId(
          groupSettingDetail.subscription.subscriptionTypeId || 0
        );
        setStartDate(
          groupSettingDetail.subscription.startDate
            ? moment(groupSettingDetail.subscription.startDate).toDate()
            : null
        );
        setEndDate(
          groupSettingDetail.subscription.endDate
            ? moment(groupSettingDetail.subscription.endDate).toDate()
            : null
        );
        setRenewalDate(
          groupSettingDetail.subscription.nextBillingDate
            ? moment(groupSettingDetail.subscription.nextBillingDate).toDate()
            : null
        );
        setNetworksCountLimit(groupSettingDetail.networkCount || 0);
        setIsSubscriptionActive(
          groupSettingDetail.subscription.status == "active" ? true : false
        );
        setIsSendTagAndAudienceSegmToGAM(
          groupSettingDetail.isSendTagsAndAudienceToGAM ? true : false
        );
        setHideSidedBranding(
          groupSettingDetail.hideSidedBranding ? true : false
        );
        setIsAiResponseTagging(
          groupSettingDetail.isAiResponseTagging ? true : false
        );
      } else {
        alert(
          "There is a problem in fetching organization detail. Please try again!"
        );
      }
    } catch (err) {
      alert(
        "There is a problem in fetching organization detail. Please try again!!!!!"
      );
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    const fetchDataAsync = async () => {
      try {
        const isAdmin = await isUserHasValidRoleForGroup(groupId, "ADMIN");
        setIsLoggedInUserAdmin(isAdmin);
        const isSuperAdmin = await isUserHasValidRoleForGroup(0, "SUPERADMIN");
        setIsLoggedInUserSuperAdmin(isSuperAdmin);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
    };
    fetchDataAsync();
  }, [groupId]);

  useEffect(() => {
    // Fetch existing settings for the group using groupId
    fetchSubscriptionTypes();
    fetchOrganizationSettings(groupId);
  }, [groupId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (iconRef.current && !iconRef.current.contains(e.target)) {
        setShowTooltip(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const convertDateStrToFormattedDate = (isoDateStr) => {
    return moment(isoDateStr).format("MMM DD, YYYY");
  };

  const convertDateStrToFormattedDateAndTime = (isoDateStr) => {
    return moment(isoDateStr).format("MMM DD, YYYY hh:mm A");
  };

  useEffect(() => {
    const getAIReqRespLimitData = async () => {
      const isAiRequestLimitData = await fetchAiRequestLimitDetail(groupId);
      setAiReqData(isAiRequestLimitData);
      setSubscriptionStartDate(
        convertDateStrToFormattedDate(
          isAiRequestLimitData.subscriptionPeriod.startDate
        )
      );
      setSubscriptionEndDate(
        convertDateStrToFormattedDate(
          isAiRequestLimitData.subscriptionPeriod.endDate
        )
      );
      setSubscriptionEndDateWithTime(
        convertDateStrToFormattedDateAndTime(
          isAiRequestLimitData.subscriptionPeriod.endDate
        )
      );
      // setSubscriptionStartDate();
      const isAiResponseLimitData = await fetchAiResponseLimitDetail(groupId);
      setAiRespData(isAiResponseLimitData);
    };
    getAIReqRespLimitData();
  }, [groupId]);

  useEffect(() => {
    const checkHideSidedBrandingFeatureLock = async () => {
      const isLocked = await isSubscriptionFeatureLocked(
        "hide-sided-branding",
        true
      );
      setIsFeatureLockHideSidedBranding(isLocked);
    };
    checkHideSidedBrandingFeatureLock();
  }, []);

  const onManageSubscription = async () => {
    const response = await manageSubscriptionPlan(groupId);
    if (response.data.status === 1) {
      if (response.data.data?.url) {
        window.open(response.data.data.url, "_blank");
      }
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!subscriptionTypeId) {
      alert("Please select subscription type.");
      return;
    }
    setLoading(true);
    const data = {
      subscriptionTypeId: subscriptionTypeId,
      networkCount: networksCountLimit,
      annualResponses: 0,
      annualAiReports: 0,
      monthlyResponses: 0,
      monthlyAiReports: 0,
      organizationName: organizationName,
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : null,
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : null,
      renewalDate: renewalDate
        ? moment(renewalDate).format("YYYY-MM-DD")
        : null,
      isSubscriptionActive: isSubscriptionActive ? "active" : "inactive",
      hideSidedBranding: hideSidedBranding,
      isSendTagsAndAudienceToGAM: isSendTagAndAudienceSegmToGAM,
      isAiResponseTagging: isAiResponseTagging,
    };
    if (subscriptionMetaDetails?.billingCycle === "monthly") {
      data.monthlyResponses = durationResponses;
      data.monthlyAiReports = durationAiRequests;
    } else {
      data.annualResponses = durationResponses;
      data.annualAiReports = durationAiRequests;
    }
    // Save logic here
    const url = `${NEW_API_URL}/master/group-settings/group/${groupId}`;
    await axios
      .put(url, data)
      .then((res) => {
        if (res.data.status) {
          setSuccessMsg("Settings saved successfully!");
          setTimeout(() => setSuccessMsg(""), 3000);
        }
        fetchOrganizationSettings(groupId);
        setLoading(false);
      })
      .catch((err) => {
        alert(
          "There is a problem in saving organization detail. Please try again!"
        );
        console.error(err);
      });
  };

  const onHandlePlan = async (planId, billingCycle) => {
    setLoading(true);
    try {
      const data = {
        subscriptionTypeId: planId,
        billingCycle: billingCycle,
        groupId: groupId,
      };
      let res;
      if (String(subscriptionTypeId) === "5") {
        res = await buySubscriptionPlan(data);
      } else {
        res = await changeSubscriptionPlan(groupId, data);
      }
      if (res.data.status === 1) {
        if (res.data.data?.url) {
          window.location.href = res.data.data.url;
        } else {
          handleSuccess("Plan upgraded successfully!");
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      }
    } catch (error) {
      console.error("Error upgrading subscription plan:", error);
      // setPaymentResult("failed");
    }
    setLoading(false);
  };

  return (
    <>
      <div style={{ marginBottom: "0" }}>
        <section
          className={loading ? "loading" : ""}
          style={{ marginBottom: 0 }}
        >
          {successMsg && <div className="sucessMsg">{successMsg}</div>}

          {paymentResult === "success" && (
            <div
              className="alert alert-success"
              style={{
                margin: "10px 0",
                padding: "15px",
                borderRadius: "4px",
                backgroundColor: "#d4edda",
                border: "1px solid #c3e6cb",
                color: "#155724",
              }}
            >
              Plan upgraded successfully!
            </div>
          )}
          {paymentResult === "failed" && (
            <div
              className="alert alert-danger"
              style={{
                margin: "10px 0",
                padding: "15px",
                borderRadius: "4px",
                backgroundColor: "#f8d7da",
                border: "1px solid #f5c6cb",
                color: "#721c24",
              }}
            >
              Payment failed. Plan upgrade did not complete.
            </div>
          )}

          <div className="page-headline is-flex">
            <div>
              <strong>Organization Settings</strong>
            </div>
            <div className="buttons">
              <Button onClick={handleSave} className="btn btn-primary">
                Save Settings
              </Button>
            </div>
          </div>

          <Form className="WLSettings common-form row" onSubmit={handleSave}>
            <Col md={6} className="pe-4">
              <Form.Group className="mb-3">
                <Form.Label>Organization Name</Form.Label>
                <Form.Control
                  type="text"
                  id="organizationName"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  placeholder="Enter organization name"
                  maxLength={100}
                  disabled={!isLoggedInUserAdmin}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Subscription Type</Form.Label>
                {isSubscriptionActive ? (
                  //-- add a drop down here based on subscriptionTypes
                  <Form.Select
                    id="subscriptionType"
                    value={subscriptionTypeId}
                    onChange={(e) =>
                      setSubscriptionTypeId(Number(e.target.value))
                    }
                    disabled={!isLoggedInUserSuperAdmin}
                    required
                    className="form-control"
                  >
                    <option value="0">Select Subscription Type</option>
                    {Object.entries(subscriptionTypes).map(([id, name]) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    type="text"
                    id="subscriptionType"
                    value={subscriptionTypeId || "Select Subscription Type"}
                    readOnly
                    plaintext
                    className="form-control"
                  />
                )}
                <div style={{ textAlign: "left", marginTop: "10px" }}>
                  <span
                    onClick={() => setSubscriptionModalVisible(true)}
                    tabIndex="-1"
                    aria-disabled="true"
                    style={{
                      color: "#007bff", // Bootstrap blue
                      fontWeight: 500,
                      paddingLeft: 0,
                      cursor: "pointer",
                    }}
                  >
                    Update subscription
                  </span>
                  {String(subscriptionTypeId) !== "5" && (
                    <span
                      onClick={() => onManageSubscription()}
                      tabIndex="-1"
                      aria-disabled="true"
                      style={{
                        color: "#007bff", // Bootstrap blue
                        fontWeight: 500,
                        paddingLeft: 0,
                        cursor: "pointer",
                        float: "right",
                      }}
                    >
                      Manage subscription
                    </span>
                  )}
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <div className="d-flex gap-2">
                  <div style={{ flex: 1 }}>
                    <Form.Label>Start Date</Form.Label>
                    {isSubscriptionActive ? (
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        className="form-control"
                        placeholderText="Start Date"
                        dateFormat="MM/dd/yyyy"
                        disabled={!isLoggedInUserSuperAdmin}
                        required
                      />
                    ) : (
                      <Form.Control
                        type="text"
                        value={
                          startDate
                            ? moment(startDate).format("MM/DD/YYYY")
                            : ""
                        }
                        readOnly
                        plaintext
                        className="form-control"
                      />
                    )}
                  </div>

                  <div style={{ flex: 1 }}>
                    <Form.Label>End Date</Form.Label>
                    {isSubscriptionActive ? (
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        className="form-control"
                        placeholderText="End Date"
                        dateFormat="MM/dd/yyyy"
                        disabled={!isLoggedInUserSuperAdmin}
                        required
                      />
                    ) : (
                      <Form.Control
                        type="text"
                        value={
                          endDate ? moment(endDate).format("MM/DD/YYYY") : ""
                        }
                        readOnly
                        plaintext
                        className="form-control"
                      />
                    )}
                  </div>

                  <div style={{ flex: 1 }}>
                    <Form.Label>Renewal Date</Form.Label>
                    {isSubscriptionActive ? (
                      <DatePicker
                        selected={renewalDate}
                        onChange={(date) => setRenewalDate(date)}
                        className="form-control"
                        placeholderText="Renewal Date"
                        dateFormat="MM/dd/yyyy"
                        disabled={!isLoggedInUserSuperAdmin}
                        required
                      />
                    ) : (
                      <Form.Control
                        type="text"
                        value={
                          renewalDate
                            ? moment(renewalDate).format("MM/DD/YYYY")
                            : ""
                        }
                        readOnly
                        plaintext
                        className="form-control"
                      />
                    )}
                  </div>
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <div className="d-flex align-items-center gap-2">
                  <Form.Check
                    type="checkbox"
                    id="isSubscriptionActive"
                    checked={isSubscriptionActive}
                    onChange={(e) => setIsSubscriptionActive(e.target.checked)}
                    disabled={!isLoggedInUserSuperAdmin}
                    label=""
                  />
                  <Form.Label
                    htmlFor="isSubscriptionActiveCheckbox"
                    className="mb-0"
                  >
                    Is Subscription Active
                  </Form.Label>
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <div className="d-flex align-items-center gap-2">
                  <Form.Check
                    type="checkbox"
                    id="isSendTagAndAudienceSegmToGAM"
                    checked={isSendTagAndAudienceSegmToGAM}
                    onChange={(e) => setIsSendTagAndAudienceSegmToGAM(e.target.checked)}
                    // disabled={!isSendTagAndAudienceSegmToGAM}
                    label=""
                  />
                  <Form.Label htmlFor="isSendTagAndAudienceSegmToGAM" className="mb-0">
                    Send Tags and Audience Segments to GAM
                  </Form.Label>
                </div>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Networks Count Limit</Form.Label>
                <Form.Control
                  type="number"
                  id="networksCountLimit"
                  value={networksCountLimit}
                  onChange={(e) => setNetworksCountLimit(e.target.value)}
                  placeholder="Enter networks count limit"
                  min="0"
                  disabled={!isLoggedInUserSuperAdmin}
                  required
                />
              </Form.Group>

              <Form.Group controlId="annualResponses" className="mb-3">
                <Form.Label>
                  {subscriptionMetaDetails?.billingCycle === "monthly"
                    ? "Monthly"
                    : "Annual"}{" "}
                  Responses
                </Form.Label>
                <Form.Control
                  type="number"
                  value={durationResponses}
                  onChange={(e) => setDurationResponses(e.target.value)}
                  placeholder="Enter annual responses"
                  min="0"
                  disabled={!isLoggedInUserSuperAdmin}
                  required
                />
              </Form.Group>

              <Form.Group controlId="annualAiRequests" className="mb-3">
                <Form.Label>
                  {subscriptionMetaDetails?.billingCycle === "monthly"
                    ? "Monthly"
                    : "Annual"}{" "}
                  AI Requests
                </Form.Label>
                <Form.Control
                  type="number"
                  value={durationAiRequests}
                  onChange={(e) => setDurationAiRequests(e.target.value)}
                  placeholder="Enter annual AI requests"
                  min="0"
                  disabled={!isLoggedInUserSuperAdmin}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <div className="d-flex align-items-center gap-2">
                  <span
                    onClick={() =>
                      handleSubscriptionAcessPopup(
                        isFeatureLockHideSidedBranding
                      )
                    }
                  >
                    <Form.Check
                      type="checkbox"
                      id="hideSidedBranding"
                      checked={hideSidedBranding}
                      onChange={(e) => setHideSidedBranding(e.target.checked)}
                      disabled={isFeatureLockHideSidedBranding}
                      label=""
                    />
                  </span>
                  <Form.Label htmlFor="hideSidedBranding" className="mb-0">
                    Hide Sided Branding{" "}
                    {isFeatureLockHideSidedBranding ? (
                      <span
                        onClick={() =>
                          handleSubscriptionAcessPopup(
                            isFeatureLockHideSidedBranding
                          )
                        }
                      >
                        <SvgElement name="lock-icon" />
                      </span>
                    ) : (
                      ""
                    )}
                  </Form.Label>
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <div className="d-flex align-items-center gap-2">
                  <Form.Check
                    type="checkbox"
                    id="isAiResponseTagging"
                    checked={isAiResponseTagging}
                    onChange={(e) => setIsAiResponseTagging(e.target.checked)}
                    label=""
                  />
                  <Form.Label htmlFor="isAiResponseTagging" className="mb-0">
                    AI Response Tagging
                  </Form.Label>
                </div>
              </Form.Group>
            </Col>
          </Form>
        </section>
        <div style={{ marginTop: "0", paddingTop: "0" }}>
          <div className="page-headline is-flex">
            <div>
              <span
                style={{ height: "40px", fontSize: "18px", fontWeight: "640" }}
              >
                Organization usage |
              </span>
              <span style={{ height: "40px", fontSize: "15px" }}>
                &nbsp;{subscriptionStartDate} - {subscriptionEndDate}
              </span>
            </div>
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="12" x2="12" y2="8" />
                <line x1="12" y1="12" x2="15" y2="12" />
              </svg>
              <span
                style={{ height: "40px", fontSize: "18px", fontWeight: "640" }}
              >
                &nbsp;Usage Renewal on:
              </span>
              <span
                style={{
                  height: "40px",
                  fontSize: "15px",
                  position: "relative",
                }}
              >
                &nbsp;
                {moment(subscriptionEndDateWithTime)
                  .add(1, "days")
                  .format("MMM DD, YYYY hh:mm A")}
                &nbsp;
                <svg
                  ref={(target) => (iconRef.current = target)}
                  onClick={() => setShowTooltip(!showTooltip)}
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ cursor: "pointer" }}
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12" y2="8" />
                </svg>
                <Overlay
                  target={iconRef.current}
                  show={showTooltip}
                  placement="top"
                >
                  {(props) => (
                    <Tooltip id="tooltip-top" {...props}>
                      Usage Renewal on:{" "}
                      {moment(subscriptionEndDateWithTime)
                        .add(1, "days")
                        .format("MMM DD, YYYY hh:mm A")}
                    </Tooltip>
                  )}
                </Overlay>
              </span>
            </div>
          </div>
          <div className="usage-block">
            <div
              style={{ height: "40px", fontSize: "20px", fontWeight: "640" }}
            >
              AI Request Usage
            </div>
            <div
              style={{ height: "30px", fontSize: "15px", fontWeight: "600" }}
            >
              {aiReqData && aiReqData.consumedQuota
                ? aiReqData.consumedQuota.toLocaleString()
                : 0}
              &nbsp;of{" "}
              {aiReqData && aiReqData.quotaLimit
                ? aiReqData.quotaLimit.toLocaleString()
                : 0}{" "}
              /{" "}
              {subscriptionMetaDetails?.billingCycle === "monthly"
                ? "mo"
                : "yr"}
            </div>

            <div className="d-flex align-items-center">
              {/* Progress Bar */}
              <div className="flex-grow-1">
                <div className="progress" style={{ height: 10, width: "100%" }}>
                  <div
                    className="progress-bar bg-primary"
                    role="progressbar"
                    style={{
                      width: `${((aiReqData && aiReqData.consumedQuota ? aiReqData.consumedQuota : 0) / (aiReqData && aiReqData.quotaLimit ? aiReqData.quotaLimit : 0)) * 100}%`,
                    }}
                    aria-valuenow={
                      aiReqData && aiReqData.consumedQuota
                        ? aiReqData.consumedQuota
                        : 0
                    }
                    aria-valuemin={0}
                    aria-valuemax={
                      aiReqData && aiReqData.quotaLimit
                        ? aiReqData.quotaLimit
                        : 0
                    }
                  ></div>
                </div>
              </div>

              <button
                className="btn btn-primary ms-3"
                style={{
                  width: "90px",
                  height: "40px",
                  fontSize: "0.9rem",
                  lineHeight: "1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                }}
                onClick={() => setSubscriptionModalVisible(true)}
              >
                Buy more
              </button>
            </div>
          </div>

          {/* <div style={{height: "40px", fontSize: "17px", fontWeight:"450", marginTop: "32px", marginBottom: "26px"}}>
          <span>
            Credits are the currency used to access emails, mobile numbers and enrichment data.
          </span>
          <span onClick={() => handleBuyMoreClick()} style={{ color: "blue", cursor: "pointer", textDecoration: "underline" }}>
            Learn more
          </span>
        </div> */}

          <div className="usage-block mb-3">
            <div
              style={{ height: "40px", fontSize: "20px", fontWeight: "640" }}
            >
              Response Usage
            </div>
            <div
              style={{ height: "30px", fontSize: "15px", fontWeight: "600" }}
            >
              {aiRespData && aiRespData.consumedQuota
                ? aiRespData.consumedQuota.toLocaleString()
                : 0}
              &nbsp;of{" "}
              {aiRespData && aiRespData.quotaLimit
                ? aiRespData.quotaLimit.toLocaleString()
                : 0}{" "}
              /{" "}
              {subscriptionMetaDetails?.billingCycle === "monthly"
                ? "mo"
                : "yr"}
            </div>

            <div className="d-flex align-items-center">
              {/* Progress Bar */}
              <div className="flex-grow-1">
                <div className="progress" style={{ height: 10, width: "100%" }}>
                  <div
                    className="progress-bar bg-primary"
                    role="progressbar"
                    style={{
                      width: `${((aiRespData && aiRespData.consumedQuota ? aiRespData.consumedQuota : 0) / (aiRespData && aiRespData.quotaLimit ? aiRespData.quotaLimit : 0)) * 100}%`,
                    }}
                    aria-valuenow={
                      aiRespData && aiRespData.consumedQuota
                        ? aiRespData.consumedQuota
                        : 0
                    }
                    aria-valuemin={0}
                    aria-valuemax={
                      aiRespData && aiRespData.quotaLimit
                        ? aiRespData.quotaLimit
                        : 0
                    }
                  ></div>
                </div>
              </div>

              <button
                className="btn btn-primary ms-3"
                style={{
                  width: "90px",
                  height: "40px",
                  fontSize: "0.9rem",
                  lineHeight: "1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                }}
                onClick={() => setSubscriptionModalVisible(true)}
              >
                Buy more
              </button>
            </div>
          </div>
        </div>
        
        <div>
          <ArchiveOrganization groupId={groupId} />
        </div>
        <div>&nbsp;</div>
      </div>
      {subscriptionModalVisible && (
        <SubscriptionModal
          open={subscriptionModalVisible}
          billingType={subscriptionMetaDetails?.billingCycle}
          onOpenChange={() => setSubscriptionModalVisible(false)}
          currentPlan={subscriptionTypeId}
          onSelectPlan={(planId, billingCycle) =>
            onHandlePlan(planId, billingCycle)
          }
          onManageSubscription={onManageSubscription}
        />
      )}
    </>
  );
};

export default GroupOrganizationSettings;
